// src/components/CustomerLoginPage.js - UPDATED VERSION
import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
  CircularProgress,
  Fade,
  Stack,
  Divider,
  Avatar,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";

function CustomerLoginPage({ onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  
  const { loading, error, setLoading, setError } = useCustomerAuth();

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
    }
    if (!password) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // This is now the ONLY action required. The onAuthStateChanged listener in
      // AuthProvider and the new useEffect in App.js will handle the rest.
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      // Provide a more secure and generic error message.
      const errorMessage = "Invalid email or password. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // TODO: Implement password reset
    alert("Please contact support@humbertrack.co.uk for password reset.");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #d97706 0%, #020617 45%, #020617 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background:
            "radial-gradient(circle at 20% 50%, #f59e0b 0%, transparent 50%), radial-gradient(circle at 80% 80%, #ef4444 0%, transparent 50%)",
          animation: "pulse 8s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 0.1 },
            "50%": { opacity: 0.2 },
          },
        }}
      />

      {/* Back button */}
      {onBack && (
        <IconButton
          onClick={onBack}
          sx={{
            position: 'absolute',
            top: 24,
            left: 24,
            color: 'white',
            bgcolor: 'rgba(15,23,42,0.8)',
            '&:hover': {
              bgcolor: 'rgba(15,23,42,0.95)',
            },
            zIndex: 10,
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}

      <Fade in timeout={800}>
        <Paper
          elevation={10}
          sx={{
            maxWidth: 440,
            width: "100%",
            p: 4,
            borderRadius: 4,
            background:
              "linear-gradient(145deg, rgba(15,23,42,0.95), rgba(15,23,42,0.9))",
            boxShadow:
              "0 20px 60px rgba(15,23,42,0.9), 0 0 0 1px rgba(245,158,11,0.4)",
            backdropFilter: "blur(10px)",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Logo and Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                boxShadow: '0 8px 24px rgba(245,158,11,0.4)',
              }}
            >
              <BusinessIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                background: "linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Customer Portal
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              HumberTrack Service Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track jobs, view invoices & request services
            </Typography>
          </Box>

          {/* Error Message */}
          {error && (
            <Fade in>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            </Fade>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleLogin} noValidate>
            <TextField
              label="Customer Email"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: null });
              }}
              error={!!errors.email}
              helperText={errors.email}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "rgba(15,23,42,0.5)",
                  "&:hover": {
                    bgcolor: "rgba(15,23,42,0.7)",
                  },
                  "&.Mui-focused": {
                    bgcolor: "rgba(15,23,42,0.8)",
                  },
                },
              }}
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors({ ...errors, password: null });
              }}
              error={!!errors.password}
              helperText={errors.password}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      disabled={loading}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "rgba(15,23,42,0.5)",
                  "&:hover": {
                    bgcolor: "rgba(15,23,42,0.7)",
                  },
                  "&.Mui-focused": {
                    bgcolor: "rgba(15,23,42,0.8)",
                  },
                },
              }}
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 1, mb: 2 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Remember me
                  </Typography>
                }
              />
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleForgotPassword}
                disabled={loading}
                sx={{
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Forgot password?
              </Link>
            </Stack>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LoginIcon />
                )
              }
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                background:
                  "linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #d97706 0%, #dc2626 100%)",
                  boxShadow: "0 6px 20px rgba(245, 158, 11, 0.6)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
                "&:disabled": {
                  background: "rgba(100, 116, 139, 0.5)",
                  color: "rgba(255, 255, 255, 0.5)",
                },
              }}
            >
              {loading ? "Signing in..." : "Sign In as Customer"}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Secure Customer Access
            </Typography>
          </Divider>

          <Stack spacing={1} sx={{ textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              HumberTrack • Vehicle Technology Installations
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Need help? Contact support@humbertrack.co.uk
            </Typography>
          </Stack>
        </Paper>
      </Fade>
    </Box>
  );
}

export default CustomerLoginPage;